import { useState } from 'react'
import { Responsive } from 'react-grid-layout'
import type { Layout, LayoutItem, ResponsiveLayouts } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { WIDGET_REGISTRY } from './registry'
import type { WidgetSummary } from '@/redux/service/widgets'

const ResponsiveGridLayout = Responsive

interface WidgetGridProps {
  groupId: number
  widgets: WidgetSummary[]
}

function loadSavedLayout(groupId: number): Layout {
  try {
    const saved = localStorage.getItem(`widget-grid-layout-${groupId}`)
    return saved ? JSON.parse(saved) : []
  } catch {
    return []
  }
}

// Widgets without a saved position are stacked below whatever's already
// placed. y must stay a finite number here — react-grid-layout's usual
// "y: Infinity means auto-place at the bottom" convention doesn't survive
// JSON.stringify (Infinity serializes to null), so a value computed with
// Infinity ends up persisted as null and the layout never actually saves.
function buildInitialLayouts(
  groupId: number,
  widgets: WidgetSummary[]
): ResponsiveLayouts {
  const saved = loadSavedLayout(groupId)
  const savedById = new Map(saved.map(entry => [entry.i, entry]))

  let nextY = saved.reduce((max, entry) => Math.max(max, entry.y + entry.h), 0)

  const lg: LayoutItem[] = widgets.map(widget => {
    const key = String(widget.id)
    const existing = savedById.get(key)
    // Guards against layouts persisted by the old y: Infinity bug, where
    // y (and possibly h) were serialized as null instead of a number.
    if (
      existing &&
      Number.isFinite(existing.y) &&
      Number.isFinite(existing.h)
    ) {
      return existing
    }

    const defaultLayout = WIDGET_REGISTRY[widget.component_key]
      ?.defaultLayout ?? {
      w: 4,
      h: 6,
    }
    const item: LayoutItem = {
      i: key,
      x: 0,
      y: nextY,
      w: defaultLayout.w,
      h: defaultLayout.h,
    }
    nextY += defaultLayout.h
    return item
  })

  return { lg }
}

export function WidgetGrid({ groupId, widgets }: WidgetGridProps) {
  const [layouts, setLayouts] = useState<ResponsiveLayouts>(() =>
    buildInitialLayouts(groupId, widgets)
  )

  const handleLayoutChange = (
    _layout: Layout,
    allLayouts: ResponsiveLayouts
  ) => {
    setLayouts(allLayouts)
    localStorage.setItem(
      `widget-grid-layout-${groupId}`,
      JSON.stringify(allLayouts['lg'] ?? [])
    )
  }

  return (
    <div style={{ width: 1200 }}>
      <ResponsiveGridLayout
        className='layout'
        layouts={layouts}
        width={1200}
        // Single breakpoint only — this grid always renders at a fixed
        // 1200px width, never adapts to viewport. Multiple breakpoints
        // caused a real bug: react-grid-layout picks a breakpoint with
        // `width > breakpointWidth` (strict >), so width=1200 against
        // lg:1200 never actually matched lg — every drag silently
        // mutated the md layout while this component only ever read
        // and persisted the lg key, so nothing appeared to save.
        breakpoints={{ lg: 0 }}
        cols={{ lg: 12 }}
        rowHeight={30}
        onLayoutChange={handleLayoutChange}
        dragConfig={{ enabled: true }}
        resizeConfig={{ enabled: true }}
        margin={[16, 16]}
      >
        {widgets.map(widget => {
          const WidgetComponent =
            WIDGET_REGISTRY[widget.component_key]?.component
          return (
            <div key={String(widget.id)}>
              {WidgetComponent ? (
                <WidgetComponent />
              ) : (
                <p className='text-xs text-muted-foreground'>
                  {widget.name} — no renderer registered
                </p>
              )}
            </div>
          )
        })}
      </ResponsiveGridLayout>
    </div>
  )
}
